import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { libraryLink, type FilterLink } from '@chess/api-insights/charts';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import { HELP_API, type Help } from '@chess/api-help';
import { registerCard } from '@chess/api-dashboard';
import '@chess/api-insights/charts.css';
import { MIDDLEGAME_CONVERSION_CARD, MiddlegameConversionCard } from './MiddlegameConversionCard.tsx';
import { MiddlegameConversionAggregator } from './MiddlegameConversionAggregator.ts';
import { CONVERSION_HELP, conversionHelp } from './help.tsx';

// Middlegame card "Converting an advantage": registers its aggregator with the
// game store and its help topic, reads the result and renders
// `MiddlegameConversionCard` on the Middlegame screen's own slot
// `middlegame.why` (order 30, the second row's third card; not on Home). Marks
// open the library filtered to their games (`libraryLink`, last 30 days).
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  const aggregator = new MiddlegameConversionAggregator();
  registerAggregator(getApi, aggregator);
  void app.getApi<Help>(HELP_API).then((help) => help.register(CONVERSION_HELP, conversionHelp));
  const Card: React.FC = () => {
    const { result, state } = useAggregate(getApi, aggregator);
    const navigate = useNavigate();
    const link = React.useMemo<FilterLink>(() => libraryLink((path) => navigate(path)), [navigate]);
    // Never hidden while the store loads: the card shows skeletons for its values.
    if (state?.status !== 'ready') return state?.status === 'failed' ? null : <MiddlegameConversionCard loading />;
    return result ? <MiddlegameConversionCard result={result} link={link} /> : null;
  };
  registerCard(app, 'middlegame.why', Card, { order: 30, ...MIDDLEGAME_CONVERSION_CARD });
}
